import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@sravni/react-design-system/lib/Button';
import { Dialog } from '@sravni/react-design-system/lib/Dialog';
import { Icon } from '@sravni/react-design-system/lib/Icon';
import { Menu } from '@sravni/react-design-system/lib/Menu';
import { Space } from '@sravni/react-design-system/lib/Space';
import { TextInput } from '@sravni/react-design-system/lib/TextInput';
import { Search, Cross, TailArrowRight } from '@sravni/react-icons';
import { useStyles, useAnalytics, useBoolean, usePrevious } from '@sravni/react-utils';
import { URLS } from '../../constants/urls.js';
import SearchSuggestsStatus from '../../enums/SearchSuggestsStatus.js';
import { useDebounceCallback } from '../../utils/useDebounceCallback.js';
import { ErrorResponse } from '../ErrorResponse/ErrorResponse.js';
import { MenuItem } from './MenuItem.js';
import modules_1ba53a2f from './styles/GlobalSearch.module.less.js';

// eslint-disable-next-line max-statements
const GlobalSearch = React.forwardRef((props) => {
    const { searchServiceHost, asPath, className, ...restProps } = props;
    const cx = useStyles(modules_1ba53a2f);
    const { pushEvent } = useAnalytics();
    const [isVisible, setIsVisible] = useBoolean(false);
    const [status, setStatus] = useState(SearchSuggestsStatus.Default);
    const [suggests, setSuggest] = useState([]);
    const [query, setQuery] = useState('');
    const prevQuery = usePrevious(query);
    const isErrorStatus = status === SearchSuggestsStatus.Error;
    const isLoadingStatus = status === SearchSuggestsStatus.Loading;
    const mainEvent = 'mainEvent';
    const eventCategory = 'Поиск';
    const serviceUrl = new URL(URLS.SEARCH_REQUEST, searchServiceHost);
    const serviceLoggerUrl = new URL(URLS.SEARCH_LOGGER, searchServiceHost);
    const searchParams = new URLSearchParams();
    searchParams.set('int_source', 'search');
    searchParams.set('int_medium', 'search');
    searchParams.set('int_term', query);
    const campaignSearchKey = 'int_campaign';
    const loadSuggest = useDebounceCallback(async () => {
        try {
            if (!(query.length >= 1)) {
                setSuggest([]);
                return;
            }
            if (prevQuery !== query) {
                setStatus(SearchSuggestsStatus.Loading);
                const data = await fetch(serviceUrl, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        limit: 5,
                        searchRequest: query,
                        sortDirection: 'asc',
                    }),
                }).then((response) => response.json());
                setStatus(SearchSuggestsStatus.Default);
                setSuggest(data.items);
            }
        }
        catch {
            setStatus(SearchSuggestsStatus.Error);
        }
    }, 300);
    useEffect(() => {
        loadSuggest();
    }, [query, loadSuggest]);
    const onReset = useCallback(() => {
        setQuery('');
    }, []);
    const handleChange = (event) => {
        setQuery(event.currentTarget.value);
    };
    const handleIconClick = useCallback(() => {
        setIsVisible.toggle();
        pushEvent({
            event: mainEvent,
            eventCategory,
            eventAction: 'Вызов поиска',
            eventLabel: 'Место вызова - Шапка',
        });
    }, [setIsVisible, pushEvent]);
    const handleSuggestClick = useCallback(async (url, label) => {
        await fetch(serviceLoggerUrl, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                query,
                requestType: 'suggest',
                redirectUrl: url,
                asPath,
                label,
            }),
        });
        window.open(url, '_self');
        setIsVisible.toggle();
        pushEvent({
            event: mainEvent,
            eventCategory,
            eventAction: 'Отправка запроса|Тип выдачи - Авторедирект',
            eventLabel: `Запрос - ${query}`,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, asPath, setIsVisible, pushEvent]);
    const handleClick = useCallback(async () => {
        const currentUrl = window?.location?.origin;
        if (suggests.length) {
            const [{ url, searchRequest }] = suggests;
            const citeRedirectUrl = new URL(url);
            searchParams.set(campaignSearchKey, 'autoredirect');
            searchParams.set('int_content', window?.location?.pathname);
            citeRedirectUrl.search = searchParams.toString();
            pushEvent({
                event: mainEvent,
                eventCategory,
                eventAction: 'Отправка запроса|Тип выдачи - Авторедирект',
                eventLabel: `Запрос - ${query}`,
            });
            await fetch(serviceLoggerUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    query,
                    requestType: 'suggest',
                    redirectUrl: citeRedirectUrl.toString(),
                    asPath,
                    label: searchRequest,
                }),
            });
            window.open(citeRedirectUrl.toString(), '_self');
        }
        else {
            const googleRedirectUrl = new URL(`/poisk/`, currentUrl);
            searchParams.set('q', query);
            searchParams.set(campaignSearchKey, 'classical');
            searchParams.set('int_content', window?.location?.pathname);
            googleRedirectUrl.search = searchParams.toString();
            await fetch(serviceLoggerUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    query,
                    requestType: 'search',
                    redirectUrl: googleRedirectUrl.toString(),
                    asPath,
                }),
            });
            pushEvent({
                event: mainEvent,
                eventCategory,
                eventAction: 'Отправка запроса|Тип выдачи - Классическая',
                eventLabel: `Запрос - ${query}`,
            });
            window.open(googleRedirectUrl.toString(), '_self');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggests, query, asPath, pushEvent, searchParams]);
    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleClick();
        }
    };
    return (React.createElement("div", { "data-qa": "GlobalSearch", className: cx(className, 'container'), ...restProps },
        React.createElement(Icon, { size: 16, className: cx('h-cursor-pointer', 'iconWrapper'), background: "gray", onClick: handleIconClick },
            React.createElement(Search, { className: cx('icon') })),
        React.createElement("div", { className: modules_1ba53a2f.search },
            React.createElement("div", { className: "gcse-search" })),
        React.createElement(Dialog, { className: cx('dialog'), visible: isVisible, onClose: setIsVisible.toggle, closable: false }, isErrorStatus ? (React.createElement(ErrorResponse, { onClick: () => setStatus(SearchSuggestsStatus.Default) })) : (React.createElement(Dialog.Content, { className: cx('dialogContent') },
            React.createElement(Space, { direction: "vertical" },
                React.createElement(Space, { size: 8, direction: "horizontal" },
                    React.createElement(TextInput, { leftIcon: React.createElement(Search, null), placeholder: "\u0427\u0442\u043E \u0432\u044B \u0445\u043E\u0442\u0438\u0442\u0435 \u043D\u0430\u0439\u0442\u0438?", value: query, onChange: handleChange, onKeyDown: onKeyDown, icon: query ? React.createElement(Icon, { icon: React.createElement(Cross, null), onClick: onReset, className: cx('crossIcon') }) : null, "aria-selected": true, autoFocus: true, loading: isLoadingStatus }),
                    React.createElement(Button, { className: cx('iconButton'), variant: "primary", onClick: handleClick },
                        React.createElement(Icon, { icon: React.createElement(TailArrowRight, null) }))),
                React.createElement(Menu, null, suggests.map((item) => {
                    const url = new URL(item.url);
                    searchParams.set(campaignSearchKey, 'autoredirect');
                    searchParams.set('int_content', window?.location?.pathname);
                    url.search = searchParams.toString();
                    return (React.createElement(MenuItem, { key: item.id, onClick: () => handleSuggestClick(url.toString(), item.searchRequest) }, item.searchRequest));
                }))))))));
});
GlobalSearch.displayName = 'GlobalSearch';

export { GlobalSearch };
//# sourceMappingURL=GlobalSearch.js.map
